import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import { QUERY_CASE_DETAIL } from "api/queries/cases";
import FormTitle from "components/Form/Title";
import GenericError from "components/GenericError";
import Loading from "components/Loading";
import PageBreadcrumb from "components/Page/Breadcrumb";
import usePageTitle from "hooks/usePageTitle";
import { APP_URLS } from "settings";
import { renderDate } from "utils/helpers";
import Container from "../../components/Page/Container";
import Applications from "./components/Applications";
import Illustrations from "./components/Illustrations";
import Notifications from "./components/Notifications";

interface CaseDetailData {
  cases: {
    case: {
      uuid: string;
      reference: string;
      statusDisplay: string;
      status: string;
      broker: string;
      applicantNames: string;
      createdOn: string;
      illustrations: any[];
      applications: any[];
    };
  };
}

type SectionProps = {
  title: string;
  children: JSX.Element;
};

const Section = ({ title, children }: SectionProps) => {
  return (
    <div className="flex flex-col space-y-4">
      <FormTitle left title={title} mb={false} />
      {children}
    </div>
  );
};

const CaseDetail: React.FC = () => {
  const { caseId } = useParams<{
    caseId: string;
  }>();

  const { loading, error, data } = useQuery<CaseDetailData>(QUERY_CASE_DETAIL, {
    variables: {
      uuid: caseId,
    },
    fetchPolicy: "no-cache",
  });

  const detail = data?.cases?.case;
  usePageTitle(detail?.reference && `Case ${detail.reference}`);

  const [applicantOneName, applicantTwoName] =
    detail?.applicantNames.split(" and ") ?? [];
  const summaryItems = [
    { label: "Applicant 1", value: applicantOneName },
    { label: "Applicant 2", value: applicantTwoName },
    { label: "Broker", value: detail?.broker },
    { label: "Status", value: detail?.statusDisplay },
  ];

  const renderCaseInfo = () => (
    <div className="mb-6 md:mb-0 md:basis-1/4">
      <div className="flex flex-col space-y-4 rounded-lg border border-gray-200 bg-white p-6 md:p-8">
        <span>
          <span className="font-semibold">Reference:</span>
          <br />
          <span>{detail?.reference}</span>
        </span>
        {detail?.statusDisplay && (
          <span>
            <span className="font-semibold">Status:</span>
            <br />
            <span>{detail?.statusDisplay}</span>
          </span>
        )}
        <span>
          <span className="font-semibold">Broker:</span>
          <br />
          <span>{detail?.broker}</span>
        </span>
        <span>
          <span className="font-semibold">Applicant(s):</span>
          <br />
          <span>{detail?.applicantNames}</span>
        </span>
        <span>
          <span className="font-semibold">Created(s):</span>
          <br />
          <span>{renderDate(detail?.createdOn)}</span>
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Container>
          <Loading />
        </Container>
      );
    }

    if (data && !data.cases?.case) {
      return (
        <Container>
          <GenericError error={error} message="Case not found" />
        </Container>
      );
    }

    return (
      <>
        {detail?.illustrations && (
          <Section title="Illustrations">
            <Illustrations illustrations={detail.illustrations} />
          </Section>
        )}
        {detail?.applications && (
          <Section title="Applications">
            <Applications
              caseDetail={detail}
              applications={detail.applications}
            />
          </Section>
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb
        items={[
          { label: "Cases", to: APP_URLS.CASE_MANAGEMENT },
          { label: detail?.reference ?? "" },
        ]}
      />

      <div className="min-h-[calc(100vh-400px)] md:basis-3/4">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <h1 className="mb-4 text-3xl font-bold">{detail?.reference}</h1>

          <div className="mb-8 rounded-lg bg-brand-green-dark p-6 text-white md:p-8">
            <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {summaryItems.map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-xs uppercase opacity-75">{label}</dt>
                  <dd className="mt-1 font-semibold">{value || "-"}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="grid md:grid-cols-4 md:gap-8">
            {renderCaseInfo()}
            <div className="space-y-8 px-0 md:col-span-3">
              <Notifications />
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CaseDetail;
